import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Box,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Alert,
} from "@mui/material";
import { TuneOutlined } from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";
import FilterSelect from "../components/FilterSelect";
import "./Filters.css";

import { useAppContext } from "../context/AppContext";

import { getTemporadas } from "../services/temporadasService";
import { getFederaciones } from "../services/federacionesService";
import { getCompeticionesByTemporada } from "../services/competicionesService";
import { getGruposByCompeticion } from "../services/gruposService";
import { getJornadasByIdGrupoAndTemporada } from "../services/jornadasService";

const EMPTY_GROUPS = [];
const noop = () => {};
const getFederationOrder = ({ order }) =>
  order != null && String(order).trim() !== '' && Number.isFinite(Number(order))
    ? Number(order)
    : Infinity;

const Filters = ({ onApplyFilters, autoApplySingleGroup = false, backButton, variant, value, onChange }) => {
  const app = useAppContext();
  const filters = value ?? app.filters;
  const setFilters = onChange ?? app.setFilters;
  const setJornadas = value === undefined ? app.setJornadas : noop;
  const queryClient = useQueryClient();
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [competicionesCategoria, setCompeticionesCategoria] = useState([]);
  const [groupMetadata, setGroupMetadata] = useState({ competition: null, options: EMPTY_GROUPS });
  const grupos = groupMetadata.competition === filters.competicion ? groupMetadata.options : EMPTY_GROUPS;
  const singleGroup = grupos.length === 1 ? grupos[0] : null;
  const [temporadas, setTemporadas] = useState([]);
  const [federaciones, setFederaciones] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metadataError, setMetadataError] = useState(false);
  const [applyError, setApplyError] = useState(false);
  const [metadataAttempt, setMetadataAttempt] = useState(0);
  const [collapsed, setCollapsed] = useState(() => variant === 'workspace' && Boolean(filters.grupo));
  const [competicionNombre, setCompeticionNombre] = useState('');
  const [grupoNombre, setGrupoNombre] = useState('');
  const navigate = useNavigate();
  const competiciones = useRef([]);
  const competitionSeason = useRef(null);
  const pendingAutoApply = useRef(null);
  const retryMetadata = useCallback(() => {
    setMetadataError(false);
    setMetadataAttempt(attempt => attempt + 1);
  }, []);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 1200 : false
  );

  useEffect(() => {
    const checkBreakpoint = () => setIsMobileOrTablet(window.innerWidth < 1200);
    window.addEventListener('resize', checkBreakpoint);
    return () => window.removeEventListener('resize', checkBreakpoint);
  }, []);

  useEffect(() => {
    if (!metadataError) return undefined;
    window.addEventListener('online', retryMetadata);
    window.addEventListener('focus', retryMetadata);
    return () => {
      window.removeEventListener('online', retryMetadata);
      window.removeEventListener('focus', retryMetadata);
    };
  }, [metadataError, retryMetadata]);

  // 🔹 Obtener competiciones por temporada
  const fetchCompeticionesByTemporada = useCallback(async (temporada) => {
    let dataCompeticiones = [];
    try {
      dataCompeticiones = await queryClient.fetchQuery({
        queryKey: ['filter-metadata', 'competitions', temporada],
        queryFn: () => getCompeticionesByTemporada(temporada, navigate),
        staleTime: 10 * 60 * 1000,
      });
      dataCompeticiones = { ...dataCompeticiones, competitions: [...dataCompeticiones.competitions]
        .sort((a, b) => parseInt(a.orden) - parseInt(b.orden)) };
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setMetadataError(true);
      return null;
    }

    return dataCompeticiones.competitions.map((competicion) => ({
      id: competicion.id,
      nombre: competicion.nombre,
      nombreCategoria: competicion.nombreGrupoCategoria,
      codGrupoCategoria: competicion.codGrupoCategoria,
      minutosJuego: competicion.minutosJuego ? Number(competicion.minutosJuego) : 90,
      federacion: String(competicion.federacion ?? ""),
    }));
  }, [navigate, queryClient]);

  // 🔹 Cargar temporadas al inicio
  useEffect(() => {
    const fetchTemporadas = async () => {
      let dataTemporadas = [];
      try {
        dataTemporadas = await queryClient.fetchQuery({
          queryKey: ['filter-metadata', 'seasons'],
          queryFn: () => getTemporadas(navigate),
          staleTime: 10 * 60 * 1000,
        });
        dataTemporadas = [...dataTemporadas].sort((a, b) => Number(b.idTemporada ?? b.id) - Number(a.idTemporada ?? a.id));
        setTemporadas(
          dataTemporadas.map((temporada) => ({
            id: temporada.idTemporada,
            name: temporada.nombre,
          }))
        );
        setLoading(false);
      } catch (error) {
        setLoading(false);
        setMetadataError(true);
      }
    };
    fetchTemporadas();
  }, [navigate, queryClient, metadataAttempt]);

  // 🔹 Cargar federaciones cuando cambia la temporada
  useEffect(() => {
    if (!filters.temporada) return;
    setFederaciones([]);
    const fetchFederaciones = async () => {
      try {
        const dataFederaciones = await queryClient.fetchQuery({
          queryKey: ['filter-metadata', 'federations'],
          queryFn: () => getFederaciones(navigate),
          staleTime: 10 * 60 * 1000,
        });
        setFederaciones([...dataFederaciones]
          .sort((a, b) => getFederationOrder(a) - getFederationOrder(b))
          .map((fed) => ({ id: fed.id, name: fed.nombre })));
      } catch (error) {
        setFederaciones([]);
        setMetadataError(true);
      }
    };
    fetchFederaciones();
  }, [filters.temporada, navigate, queryClient, metadataAttempt]);

  useEffect(() => {
    const season = temporadas.find(item => String(item.id) === String(filters.temporada));
    if (season && !filters.temporadaNombre) {
      setFilters(previous => String(previous.temporada) === String(season.id)
        ? { ...previous, temporadaNombre: season.name } : previous);
    }
  }, [temporadas, filters.temporada, filters.temporadaNombre, setFilters]);

  useEffect(() => {
    const federation = federaciones.find(item => String(item.id) === String(filters.federacion));
    if (federation && !filters.federacionNombre) {
      setFilters(previous => String(previous.federacion) === String(federation.id)
        ? { ...previous, federacionNombre: federation.name } : previous);
    }
  }, [federaciones, filters.federacion, filters.federacionNombre, setFilters]);

  useEffect(() => {
    if (!filters.temporada || !filters.competicion || (filters.federacion && filters.categoria && filters.competicionNombre)) return undefined;
    let cancelled = false;
    queryClient.fetchQuery({
      queryKey: ['filter-metadata', 'competitions', filters.temporada],
      queryFn: () => getCompeticionesByTemporada(filters.temporada, navigate),
      staleTime: 10 * 60 * 1000,
    }).then((data) => {
      const competition = data.competitions?.find((item) => String(item.id) === String(filters.competicion));
      if (!competition || cancelled) return;
      setFilters((previous) => ({ ...previous, federacion: competition.federacion,
        categoria: competition.codGrupoCategoria, categoriaNombre: competition.nombreGrupoCategoria,
        competicionNombre: competition.nombre, minutosJuego: Number(competition.minutosJuego) || 90 }));
    }).catch(() => { if (!cancelled) setMetadataError(true); });
    return () => { cancelled = true; };
  }, [filters.temporada, filters.competicion, filters.competicionNombre, filters.federacion, filters.categoria, navigate, queryClient, setFilters, metadataAttempt]);

  // 🔹 Cargar categorías solo cuando hay temporada Y federación seleccionadas
  useEffect(() => {
    if (!filters.temporada || !filters.federacion) return;
    let cancelled = false;

    const loadCategorias = async () => {
      const competicionesData = await fetchCompeticionesByTemporada(filters.temporada);
      if (!competicionesData || cancelled) return;
      competiciones.current = competicionesData;
      competitionSeason.current = filters.temporada;

      const competicionesPorFederacion = competicionesData.filter(
        (c) => String(c.federacion) === String(filters.federacion)
      );

      const categoriasData = [
        ...new Map(
          competicionesPorFederacion.map((comp) => [
            comp.codGrupoCategoria,
            {
              id: comp.codGrupoCategoria,
              name: comp.nombreCategoria,
            },
          ])
        ).values(),
      ];

      setCategorias(categoriasData);

      if (!filters.categoria && filters.competicion) return;
      if (!filters.categoria || !categoriasData.some((cat) => String(cat.id) === String(filters.categoria))) {
        setFilters((prev) => ({
          ...prev,
          categoria: null,
          categoriaNombre: "",
          competicion: null,
          grupo: null,
        }));
      } else if (filters.categoria && !filters.categoriaNombre) {
        const cat = categoriasData.find((c) => String(c.id) === String(filters.categoria));
        if (cat) {
          setFilters((prev) => ({ ...prev, categoriaNombre: cat.name }));
        }
      }
    };

    loadCategorias();
    return () => { cancelled = true; };
  }, [filters.temporada, filters.federacion, filters.categoria, filters.categoriaNombre, filters.competicion, fetchCompeticionesByTemporada, setFilters, metadataAttempt]);

  const loadCompeticiones = useCallback(async () => {
    if (!filters.temporada) return;

    let available = competiciones.current;
    if (competitionSeason.current !== filters.temporada || available.length === 0) {
      const competicionesData = await fetchCompeticionesByTemporada(filters.temporada);
      if (!competicionesData) return [];
      available = competicionesData;
    }

    const competicionesPorFederacion = filters.federacion
      ? available.filter(c => String(c.federacion) === String(filters.federacion))
      : available;

    const competicionesFiltradas = competicionesPorFederacion.filter(
      (competicion) => String(competicion.codGrupoCategoria) === String(filters.categoria)
    );

    const competicionesFiltradasCategoria = competicionesFiltradas.map(
      ({ id, nombre }) => ({
        id,
        name: nombre,
      })
    );

    return competicionesFiltradasCategoria;
  }, [filters.temporada, filters.federacion, filters.categoria, fetchCompeticionesByTemporada]);

  useEffect(() => {
    let cancelled = false;
    setCompeticionesCategoria([]);
    if (filters.categoria) {
      loadCompeticiones().then(options => { if (!cancelled) setCompeticionesCategoria(options || []); });
    }
    return () => { cancelled = true; };
  }, [filters.categoria, loadCompeticiones, metadataAttempt]);

  // Track competicion name for collapsed summary
  useEffect(() => {
    if (filters.competicion && competicionesCategoria.length > 0) {
      const comp = competicionesCategoria.find((c) => String(c.id) === String(filters.competicion));
      setCompeticionNombre(comp?.name || '');
    } else if (!filters.competicion) {
      setCompeticionNombre('');
    }
  }, [filters.competicion, competicionesCategoria]);

  const loadGrupos = useCallback(async () => {
    const gruposData = await queryClient.fetchQuery({
      queryKey: ['filter-metadata', 'groups', filters.competicion],
      queryFn: () => getGruposByCompeticion(filters.competicion, navigate),
      staleTime: 10 * 60 * 1000,
    });
    const groups = gruposData.groups.map((grupo) => ({
      id: grupo.id,
      name: grupo.nombre,
    }));

    const gruposOrdenados = [...groups].sort((a, b) => {
      const getNumeroGrupo = (str) => {
        const match = str.match(/Grupo\s+(\d+)/);
        return match ? parseInt(match[1], 10) : 0;
      };
      return getNumeroGrupo(a.name) - getNumeroGrupo(b.name);
    });

    return gruposOrdenados;
  }, [filters.competicion, navigate, queryClient]);

  useEffect(() => {
    let cancelled = false;
    if (filters.competicion) {
      loadGrupos().then((groups) => {
        if (!cancelled) setGroupMetadata({ competition: filters.competicion, options: groups });
      }).catch(() => {
        if (!cancelled) {
          setGroupMetadata({ competition: filters.competicion, options: EMPTY_GROUPS });
          setMetadataError(true);
        }
      });
    } else {
      setGroupMetadata({ competition: null, options: EMPTY_GROUPS });
    }
    return () => { cancelled = true; };
  }, [filters.competicion, loadGrupos, metadataAttempt]);

  useEffect(() => {
    if (!singleGroup || !filters.temporada || String(filters.grupo) === String(singleGroup.id)) return;
    pendingAutoApply.current = {
      temporada: filters.temporada, competicion: filters.competicion, grupo: singleGroup.id,
    };
    setFilters(previous => previous.temporada === filters.temporada && previous.competicion === filters.competicion
      ? { ...previous, grupo: singleGroup.id, grupoNombre: singleGroup.name } : previous);
  }, [singleGroup, filters.temporada, filters.competicion, filters.grupo, setFilters]);

  // Track grupo name for collapsed summary
  useEffect(() => {
    if (filters.grupo && grupos.length > 0) {
      const g = grupos.find((gr) => String(gr.id) === String(filters.grupo));
      setGrupoNombre(g?.name || '');
      if (g) setFilters((previous) => previous.grupoNombre === g.name ? previous : { ...previous, grupoNombre: g.name });
    } else if (!filters.grupo) {
      setGrupoNombre('');
    }
  }, [filters.grupo, grupos, setFilters]);

  // Reset collapsed when main filter resets
  useEffect(() => {
    if (!filters.temporada) setCollapsed(false);
  }, [filters.temporada]);

  const loadJornadas = useCallback(async () => {
    if (!filters.grupo || !filters.temporada) return;
    const jornadasData = await getJornadasByIdGrupoAndTemporada(filters.grupo, filters.temporada, navigate);
    setJornadas(jornadasData.jornadas);
  }, [filters.grupo, filters.temporada, navigate, setJornadas]);

  const handleApplyFilters = useCallback(async () => {
    setLoadingCalendar(true);
    setApplyError(false);
    try {
      if (onApplyFilters) await onApplyFilters();
      else await loadJornadas();
      if (isMobileOrTablet && filters.grupo) setCollapsed(true);
    } catch (error) {
      setApplyError(true);
    } finally {
      setLoadingCalendar(false);
    }
  }, [onApplyFilters, loadJornadas, isMobileOrTablet, filters.grupo]);

  useEffect(() => {
    const pending = pendingAutoApply.current;
    if (!pending) return;
    if (pending.temporada !== filters.temporada || pending.competicion !== filters.competicion) {
      pendingAutoApply.current = null;
      return;
    }
    if (String(pending.grupo) !== String(filters.grupo)) return;
    pendingAutoApply.current = null;
    if (variant !== 'comparison' && (autoApplySingleGroup || !onApplyFilters)) void handleApplyFilters();
  }, [filters.temporada, filters.competicion, filters.grupo, variant, autoApplySingleGroup, onApplyFilters, handleApplyFilters]);

  const setFiltersCategoria = (value, nombre) => {
    setCollapsed(false);
    setJornadas([]);
    setFilters((prevFilters) => ({
      ...prevFilters,
      categoria: value,
      categoriaNombre: nombre || "",
      competicion: "",
      competicionNombre: "",
      grupo: "",
      grupoNombre: "",
    }));
  };

  const setFiltersFederacion = (value, nombre) => {
    setCollapsed(false);
    setJornadas([]);
    setFilters((prevFilters) => ({
      ...prevFilters,
      federacion: value,
      federacionNombre: nombre || "",
      categoria: "",
      categoriaNombre: "",
      competicion: "",
      competicionNombre: "",
      grupo: "",
      grupoNombre: "",
    }));
  };

  const temporadaNombre = temporadas.find((t) => String(t.id) === String(filters.temporada))?.name
    || filters.temporadaNombre
    || '';

  const selectedFilterChips = [
    temporadaNombre && { key: 'temporada', label: temporadaNombre },
    filters.federacionNombre && { key: 'federacion', label: filters.federacionNombre },
    filters.categoriaNombre && { key: 'categoria', label: filters.categoriaNombre },
    competicionNombre && { key: 'competicion', label: competicionNombre },
    grupoNombre && { key: 'grupo', label: grupoNombre },
  ].filter(Boolean);

  if (collapsed && isMobileOrTablet && variant !== 'comparison' && !metadataError && !applyError) {
    if (variant === 'workspace') return <Box className="pm-filters-summary">
      <Typography variant="caption" fontWeight={650}>Tu competición</Typography>
      <strong>{[competicionNombre || filters.competicionNombre, grupoNombre || filters.grupoNombre].filter(Boolean).join(' · ')}</strong>
      <p>{[temporadaNombre, filters.federacionNombre || federaciones.find((item) => String(item.id) === String(filters.federacion))?.name, filters.categoriaNombre].filter(Boolean).join(' · ')}</p>
      <Button startIcon={<TuneOutlined />} onClick={() => setCollapsed(false)}>Modificar filtros</Button>
    </Box>;
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 0.5,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 0.75,
            flexWrap: 'wrap',
            flex: 1,
            minWidth: 0,
          }}
        >
          {selectedFilterChips.map((chip) => (
            <Chip
              key={chip.key}
              label={chip.label}
              size="small"
              variant="outlined"
              sx={{
                fontSize: 11,
                height: 24,
                maxWidth: 150,
                '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
              }}
            />
          ))}
          {selectedFilterChips.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: '24px' }}>
              Sin filtros aplicados
            </Typography>
          )}
        </Box>
        <Tooltip title="Modificar filtros">
          <IconButton
            size="small"
            onClick={() => setCollapsed(false)}
            sx={{ flexShrink: 0, color: 'primary.main' }}
            aria-label="Modificar filtros"
          >
            <TuneOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <>
    {metadataError && <Alert severity="error" action={<Button onClick={retryMetadata}>Reintentar</Button>}>
      No se han podido cargar los filtros de competición.
    </Alert>}
    {applyError && <Alert severity="error" action={<Button onClick={handleApplyFilters}>Reintentar</Button>}>
      No se han podido cargar los datos de la competición.
    </Alert>}
    <Box
      className={variant === 'comparison' ? 'pc-context-grid' : variant === 'workspace' ? 'pm-filters-grid' : undefined}
      data-single-group={singleGroup ? 'true' : undefined}
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(1, 1fr)",
          sm: "repeat(2, 1fr)",
          md: "repeat(3, 1fr)",
          lg: "repeat(5, 1fr)",
        },
        gap: 2,
        mb: 4,
      }}
    >
      <FilterSelect
        label="Temporada"
        value={filters.temporada}
        options={temporadas}
        onChange={(value) => {
          setCollapsed(false);
          setJornadas([]);
          setFilters({
            temporada: value.id,
            temporadaNombre: value.name,
            federacion: "",
            federacionNombre: "",
            categoria: "",
            categoriaNombre: "",
            competicion: "",
            grupo: "",
          });
        }}
      />
      <FilterSelect
        label="Federación"
        value={filters.federacion}
        options={federaciones}
        onChange={(value) => setFiltersFederacion(value.id, value.name)}
        disabled={!filters.temporada || federaciones.length === 0}
      />
      <FilterSelect
        label="Categoría competición"
        value={filters.categoria}
        options={categorias}
        onChange={(value) => setFiltersCategoria(value.id, value.name)}
        disabled={!filters.temporada || categorias.length === 0}
      />
      <FilterSelect
        label="Competiciones"
        options={competicionesCategoria}
        value={filters.competicion}
        dependencyValue={filters.categoria}
        onChange={(value) => {
          setCollapsed(false);
          setJornadas([]);
          const comp = competiciones.current.find((c) => c.id === value.id);
          setFilters((prevFilters) => ({
            ...prevFilters,
            competicion: value.id,
            competicionNombre: value.name,
            minutosJuego: comp?.minutosJuego ? Number(comp.minutosJuego) : 90,
            grupo: "",
            grupoNombre: "",
          }));
        }}
        disabled={!filters.categoria || competicionesCategoria.length === 0}
      />
      {!singleGroup && <FilterSelect
        label="Grupo"
        options={grupos}
        value={filters.grupo}
        dependencyValue={filters.competicion}
        onChange={(value) => {
          setCollapsed(false);
          setJornadas([]);
          setFilters((prevFilters) => ({ ...prevFilters, grupo: value.id, grupoNombre: value.name }));
        }}
        disabled={!filters.competicion || grupos.length === 0}
        loading={loading}
      />}
      {variant !== 'comparison' && <Box display="flex" justifyContent="center" mb={4}>
        <Button
          variant="contained"
          color="primary"
          startIcon={variant === 'workspace' ? <TuneOutlined /> : undefined}
          onClick={handleApplyFilters}
          disabled={loadingCalendar}
        >
          {loadingCalendar ? <CircularProgress size={24} /> : "Aplicar Filtros"}
        </Button>
      </Box>}
    </Box>
    </>
  );
};

export default Filters;
